/**
 * The title at the top of a working screen (trip entry, fleet, settings):
 * the same bilingual heading the reports and the dashboard carry, so every
 * screen announces itself the same way.
 *
 * `inset` (the default) is for the top of a card, padded like a report's
 * header; without it the title sits above several cards, as on Fleet.
 */
import type { ReactNode } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { useBi } from './Bi'
import { c } from '../theme'

export default function ScreenTitle({ titleKey, sub, right, inset = true }: {
  titleKey: string; sub?: ReactNode; right?: ReactNode; inset?: boolean
}) {
  const bi = useBi()
  return (
    <Stack direction="row" alignItems="flex-start" flexWrap="wrap" useFlexGap gap={1.5}
      sx={inset ? { px: 2.5, pt: 2.25, pb: 1 } : {}}>
      {/* Wide enough to wrap the actions onto their own line on a phone. */}
      <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
        <Typography component="h1" sx={{ m: 0, fontSize: inset ? 22 : 24, fontWeight: 600,
                                          letterSpacing: '-0.01em', lineHeight: 1.3 }}>
          {bi(titleKey)}
        </Typography>
        {sub && (
          <Typography component="div" sx={{ fontSize: 13, color: c.inkMuted, mt: 0.25 }}>
            {sub}
          </Typography>
        )}
      </Box>
      {right}
    </Stack>
  )
}

/** A heading inside a screen ("Identity", "Tax invoice"): smaller than the title. */
export function PanelHeading({ children, hint }: { children: ReactNode; hint?: ReactNode }) {
  return (
    <Box sx={{ pt: 1 }}>
      <Typography component="h2" sx={{ m: 0, fontSize: 15, fontWeight: 600, color: c.ink }}>
        {children}
      </Typography>
      {hint && (
        <Typography component="div" sx={{ fontSize: 12.5, color: c.inkMuted, mt: 0.25 }}>
          {hint}
        </Typography>
      )}
    </Box>
  )
}
